import type { LoadedPlugin, PluginManifest } from './types';
import { PluginRegistry } from './loader';
import { normalizePluginManifest } from './normalizePluginManifest';

// Singleton registry instance
let registryInstance: PluginRegistry | null = null;
let manifestsCache: PluginManifest[] | null = null;
let manifestsPromise: Promise<PluginManifest[]> | null = null;

function getRegistry(queryClient?: any): PluginRegistry {
	if (!registryInstance) {
		registryInstance = new PluginRegistry(queryClient);
	}
	return registryInstance;
}

/**
 * Clear the manifests cache
 * Call this after publishing, uninstalling, or enabling/disabling plugins
 */
export function clearManifestsCache(): void {
	console.log('[Plugin Registry] Clearing manifests cache');
	manifestsCache = null;
	manifestsPromise = null;
}

/**
 * Get plugin manifests only (lightweight, no bundle loading)
 * Use this for dashboard cards, recent, favorites - just metadata
 * Always returns lightweight index entries (lazy loading by default)
 */
export async function getPluginManifests(forceRefresh = false): Promise<PluginManifest[]> {
	if (forceRefresh) {
		clearManifestsCache();
	}
	
	if (manifestsCache && !forceRefresh) return manifestsCache;
	if (manifestsPromise && !forceRefresh) return manifestsPromise;
	
	const registry = getRegistry();
	manifestsPromise = registry.discover().then(manifests => {
		manifestsCache = manifests;
		return manifests;
	});
	
	return manifestsPromise;
}

/**
 * Get full manifest for a specific plugin by ID
 * Loads from individual manifest file (lazy loading)
 */
export async function getPluginManifestById(pluginId: string): Promise<PluginManifest | null> {
	const registry = getRegistry();
	const manifest = await registry.getManifestById(pluginId);
	return manifest ? normalizePluginManifest(manifest) : null;
}

/**
 * Lazy load a specific plugin bundle on-demand
 * Use this when user navigates to a plugin route or activates a widget
 * 
 * This function will:
 * 1. Get lightweight manifest from cache (if available)
 * 2. Load full manifest if needed (lazy loading)
 * 3. Load plugin bundle
 */
export async function loadPlugin(pluginId: string, queryClient?: any): Promise<LoadedPlugin | null> {
	const registry = getRegistry(queryClient);
	
	// Check if plugin is already loaded (prevents reloading on route changes)
	const alreadyLoaded = registry.getLoadedPlugin(pluginId);
	if (alreadyLoaded) {
		console.log(`[Plugin] ${pluginId}: already loaded, using cache`);
		return alreadyLoaded;
	}
	
	// Try to get manifest from cache first (lightweight index)
	let manifest = (await getPluginManifests()).find(m => m.id === pluginId);
	
	// If not found or lightweight, load full manifest
	if (!manifest || (!manifest.permissions && manifest.manifest)) {
		console.log(`[Plugin] ${pluginId}: loading full manifest...`);
		const fullManifest = await registry.getManifestById(pluginId);
		if (!fullManifest) {
			console.warn(`[Plugin] ${pluginId}: manifest not found`);
			return null;
		}
		manifest = fullManifest;
	}
	
	try {
		console.log(`[Plugin] ${pluginId}: lazy loading bundle...`);
		const plugin = await registry.load(manifest);
		return plugin;
	} catch (error) {
		console.error(`[Plugin] ${pluginId}: lazy load failed`, error);
		return null;
	}
}

/**
 * Get all loaded plugins (legacy - for backward compatibility)
 * WARNING: This eagerly loads ALL plugin bundles - use sparingly!
 * @deprecated Use getPluginManifests() + loadPlugin() for better performance
 */
export function getLoadedPlugins(queryClient?: any): Promise<LoadedPlugin[]> {
	const registry = getRegistry(queryClient);
	return (async () => {
		const manifests = await getPluginManifests();
		console.warn('[Plugin] Loading ALL plugins eagerly - consider using lazy loading');
		const plugins = await registry.loadAll(manifests);
		return plugins;
	})();
}


